import VectorLayer from "ol/layer/Vector";
import Control from "ol/control/Control";

import {
	DataWeatherStation,
	IStationWithObservation,
} from "@/schemas/observation-schema";
import { AreaPolygonType } from "@/blocks/warnings-map/types/area-polygon-type";
import { useEffect, useRef, useState } from "@wordpress/element";
import { MAP_CARD_EXTEND } from "./map-constant";
import { Extent } from "ol/extent";
import { IForecastWithStation } from "@/types/daily-forecast-type";
import Style from "ol/style/Style";

import "./style.scss";
import { useMapGeotiff } from "./hooks/use-map-geotiff";
import { IImageryState } from "./types/map-type";
import { useOpenLayersMap } from "./hooks/use-open-layer-map";
import { useMapObservationMarker } from "./hooks/use-map-observation-marker";
import { useRegions } from "./hooks/use-regions";
import { useWeatherForecastMap } from "./hooks/use-weather-forecast";
import { getCurrentLanguage } from "@/helpers/get-current-langage";

type MapContainerProps = {
	controls: Control[];
	height?: number;
	position?: [number, number];
	filter?: keyof DataWeatherStation;
	stationData?: IStationWithObservation[];
	areaPolygon?: AreaPolygonType;
	showStationLabelAndImage: boolean;
	allowInteraction: boolean;
	forecastDataWithStations?: {
		data: IForecastWithStation[];
		show: boolean;
		selectedDate: Date;
	};
	zoom?: number;
	clickOnPolygon?: (open: boolean, value?: string) => void;
	selectedPolygonId?: string;
	showRegionStroke: boolean;
	extend?: Extent;
	imageryState?: IImageryState;
};

export const MapContainer = ({
	controls,
	height = 500,
	filter,
	extend = MAP_CARD_EXTEND,
	areaPolygon,
	zoom,
	allowInteraction = true,
	selectedPolygonId,
	imageryState,
	forecastDataWithStations,
	clickOnPolygon,
}: MapContainerProps) => {
	const pathname = window.location.pathname;
	const isWarningPage = pathname.includes("warnings");
	const isObsForecastPage = pathname.includes("observation-map");
	const [mapZoom, setZoom] = useState(zoom ?? 8);

	const lang = getCurrentLanguage();
	const isArabic = lang === "ar";

	const markerLayerRef = useRef<VectorLayer | null>(null);

	const { mapRef, mapRefOl, observationSourceRef, vectorSourceRef } =
		useOpenLayersMap({
			extent: extend,
			handleChangeZoom: (value: number) => setZoom(value),
			zoom: mapZoom,
			allowInteraction,
			controls,
		});

	useMapGeotiff({
		mapRefOl,
		observationImagery: imageryState,
	});

	useMapObservationMarker({
		activeFilter: filter,
		observationSourceRef,
		imageryState,
	});

	useWeatherForecastMap({
		isObsForecastPage,
		mapRefOl,
		forecastDataWithStations,
		vectorSourceRef,
		isArabic,
	});

	useRegions({
		areaPolygon,
		mapRefOl,
		clickOnPolygon,
		filter,
		markerLayerRef,
		isWarningPage,
		selectedPolygonId,
	});

	useEffect(() => {
		const allMarkers = vectorSourceRef?.current?.getFeatures() ?? [];
		const obsMarker = observationSourceRef.current?.getFeatures() ?? [];

		const allItem = [...allMarkers, ...obsMarker];

		allItem.forEach((marker) => {
			const priority = Number(marker.get("priority"));

			let visible = false;

			if (mapZoom > 8.8) {
				visible = true;
			} else if (mapZoom > 7.7) {
				visible = priority <= 2;
			} else {
				visible = priority === 1;
			}

			if (visible) {
				marker.setStyle(marker.get("defaultStyle"));
			} else {
				marker.setStyle(new Style({}));
			}
		});
	}, [
		mapZoom,
		vectorSourceRef.current?.getFeatures(),
		observationSourceRef.current?.getFeatures(),
		areaPolygon,
	]);

	return (
		<div
			className="map-container"
			ref={mapRef}
			style={{ width: "100%", height: `${height}px` }}
		/>
	);
};

export default MapContainer;
